import { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Scramble from "../../components/Scramble/Scramble";
import LanyardGate from "../../components/Lanyard/LanyardGate";
import MaskLine from "../../components/MaskLine/MaskLine";
import SceneTrack from "../../components/Morph/SceneTrack";
import IndexMorph from "../../components/Morph/scenes/IndexScene";
import { useMagnetic } from "../../hooks/useMagnetic";
import { usePhoneScene, useStoryMode } from "../../hooks/useStoryMode";
import { useIntroMorph } from "../../lib/introMorph";
import { DUR, EASE, STAGGER, HERO_FLIP_TRANSITION } from "../../lib/motion";
import "./Home.css";

/** Module-level so Scramble's effect sees a stable reference. */
const ROLES = ["Full-Stack Developer", "Backend Developer", "Frontend Developer"];

/** Four true things, stated flatly. The restraint is the point. */
const FACTS = [
    { k: "Focus", v: "Backend · Full-stack" },
    { k: "Stack", v: ".NET · React · Vue" },
    { k: "Based", v: "Quy Nhơn, VN" },
    { k: "Status", v: "Open to work" },
];

const NAME = "Quoc Huy";
const PHOTO = "/IMG_3159.jpg";
/** The same photo pre-scaled for the info card, where it shows at ~100px: a 2944px original squeezed that far comes out soft. */
const CARD_PHOTO = "/IMG_3159-card.jpg";

/**
 * The name as one inline-block span per letter, so the Index scene can fold
 * individual letters away ("Quoc Huy" → "QH") without re-flowing the line.
 */
function Glyphs() {
    return (
        <>
            {NAME.split("").map((ch, i) => (
                <span key={i} className="glyph" data-glyph="">
                    {ch === " " ? " " : ch}
                </span>
            ))}
        </>
    );
}

export default function Home() {
    const { heroTextMode, introActive } = useIntroMorph();
    const story = useStoryMode();
    // A phone held upright plays the same scene on a compact hero (Home.css).
    const phone = usePhoneScene();
    const heroRef = useRef<HTMLElement>(null);
    const primaryRef = useRef<HTMLAnchorElement>(null);
    const secondaryRef = useRef<HTMLAnchorElement>(null);

    // Was 0.3 — strong enough that buttons visibly chased the cursor. At 0.16
    // you feel the pull without catching the trick.
    useMagnetic(primaryRef, 0.16);
    useMagnetic(secondaryRef, 0.16);

    // Portrait parallax. Framer motion values rather than GSAP: the hero used to
    // run both engines at once, on different easing curves.
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const x = useSpring(mx, { stiffness: 55, damping: 22, mass: 0.5 });
    const y = useSpring(my, { stiffness: 55, damping: 22, mass: 0.5 });

    useEffect(() => {
        if (window.matchMedia("(hover: none)").matches) return;
        const onMove = (e: PointerEvent) => {
            // ±8px: enough to feel dimensional, not enough to notice as an effect.
            mx.set((e.clientX / window.innerWidth - 0.5) * 16);
            my.set((e.clientY / window.innerHeight - 0.5) * 16);
        };
        window.addEventListener("pointermove", onMove, { passive: true });
        return () => window.removeEventListener("pointermove", onMove);
    }, [mx, my]);

    const hero = (
        <section className="hero" ref={heroRef}>
            <div className="hero-grid">
                <div className="hero-main">
                    <motion.p
                        data-im="eyebrow"
                        className="hero-eyebrow mono"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: DUR.base, delay: 0.5, ease: EASE }}
                    >
                        <span className="hero-dot" aria-hidden="true" />
                        Available for work
                    </motion.p>

                    {/*
                        Three states, driven by IntroMorphContext (see
                        lib/introMorph.tsx):
                        - "wait": Intro currently owns these as a shared-layout
                          element; render invisible placeholders of the same
                          size so the rest of the hero doesn't reflow the
                          instant the real ones land (that reflow would be
                          visible through Intro's background as it fades).
                        - "morph": the handoff has happened — render the
                          shared element Intro's copy just flew into.
                        - "normal": no intro played this load — the original
                          independent MaskLine reveal, unchanged.
                    */}
                    {heroTextMode === "wait" ? (
                        <>
                            <div data-im="name">
                                <h1 className="hero-name" style={{ visibility: "hidden" }}>
                                    <Glyphs />
                                </h1>
                            </div>
                            <div data-im="role">
                                <p className="hero-role-line" style={{ visibility: "hidden" }}>
                                    <span className="hero-role mono">Full-Stack Developer</span>
                                </p>
                            </div>
                        </>
                    ) : heroTextMode === "morph" ? (
                        <>
                            <div data-im="name">
                                <motion.h1
                                    layoutId="shared-hero-name"
                                    className="hero-name"
                                    transition={HERO_FLIP_TRANSITION}
                                >
                                    <Glyphs />
                                </motion.h1>
                            </div>
                            <div data-im="role">
                                <motion.p
                                    layoutId="shared-hero-role"
                                    className="hero-role-line"
                                    transition={HERO_FLIP_TRANSITION}
                                >
                                    <Scramble phrases={ROLES} className="hero-role mono" />
                                </motion.p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div data-im="name">
                                <h1 className="hero-name">
                                    <MaskLine delay={0.06}>
                                        <Glyphs />
                                    </MaskLine>
                                </h1>
                            </div>
                            <div data-im="role">
                                <p className="hero-role-line">
                                    <MaskLine delay={0.18}>
                                        <Scramble phrases={ROLES} className="hero-role mono" />
                                    </MaskLine>
                                </p>
                            </div>
                        </>
                    )}

                    <motion.p
                        data-im="lede"
                        className="hero-lede"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: DUR.base, delay: 0.42, ease: EASE }}
                    >
                        I design and build production systems end to end — RESTful APIs,
                        query-level performance work, and the interfaces on top of them.
                    </motion.p>

                    <motion.div
                        data-im="cta"
                        className="hero-cta"
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: DUR.base, delay: 0.54, ease: EASE }}
                    >
                        <a ref={primaryRef} href="#contact" className="btn btn-primary">
                            <span>Get in touch</span>
                            <ArrowRight size={15} strokeWidth={2} className="btn-icon" />
                        </a>
                        <a ref={secondaryRef} href="#projects" className="btn btn-secondary">
                            {/* Wrapped so `.btn > *` can lift it above the
                                sweep layer — a bare text node has no box to
                                give a z-index to, and the sweep would pass in
                                front of the label instead of behind it. */}
                            <span>View work</span>
                        </a>
                    </motion.div>
                </div>

                <motion.div
                    data-im="portrait"
                    className="hero-portrait"
                    style={{ x, y }}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: DUR.slow, delay: 0.26, ease: EASE }}
                >
                    {/*
                        One box around everything that shows the portrait, so
                        the Index scene can carry it — 3D card included, since
                        the lanyard frames itself on this box every frame — to
                        the middle of the page without touching the parallax
                        transform on the motion.div above.
                    */}
                    <div className="hero-portrait-slot" data-im="slot">
                        {/*
                            Static fallback — always in the DOM, hidden by CSS
                            only where LanyardGate actually mounts a scene (see
                            Home.css). That keeps this photo the one that shows
                            for touch devices, reduced-motion, and the brief
                            window before the 3D bundle finishes loading.
                        */}
                        <div className="portrait-frame">
                            <img src={PHOTO} alt="Portrait of Quoc Huy" className="portrait-img" />
                        </div>
                        <span className="portrait-tick portrait-tick--tl" aria-hidden="true" />
                        <span className="portrait-tick portrait-tick--br" aria-hidden="true" />

                        {/*
                            Held back until the intro has fully finished, not just
                            handed off the name/role text. A frame-timing trace
                            showed a ~550ms dropped frame at the exact instant the
                            shared-layout flight began, and this scene — a live
                            Three.js + Rapier physics render loop — was already
                            running underneath Intro's opaque background the whole
                            time before that fix, spending real frame budget on a
                            WebGL scene nobody could see for the one moment that
                            budget mattered most. It's invisible during "wait" and
                            "morph" regardless (Intro's background covers it, then
                            the flight is the only thing that should be moving),
                            so deferring its mount costs nothing visible.
                        */}
                        {!introActive && <LanyardGate frontImage={CARD_PHOTO} />}
                    </div>
                </motion.div>
            </div>

            {/*
                The strip used to fade in as one block, which made four
                separate facts read as a single grey bar arriving. Dealing them
                out left to right lets the eye actually land on each one — and
                the rule between them draws itself as its column arrives, so
                the strip assembles rather than appears.
            */}
            <motion.dl
                data-im="facts"
                className="hero-facts"
                initial="hidden"
                animate="shown"
                variants={{
                    hidden: {},
                    shown: { transition: { delayChildren: 0.7, staggerChildren: STAGGER } },
                }}
            >
                {FACTS.map((fact) => (
                    <motion.div
                        key={fact.k}
                        data-im="fact"
                        className="fact"
                        variants={{
                            hidden: { opacity: 0, y: 12 },
                            shown: {
                                opacity: 1,
                                y: 0,
                                transition: { duration: DUR.base, ease: EASE },
                            },
                        }}
                    >
                        <dt className="fact-key mono">{fact.k}</dt>
                        <dd className="fact-value">{fact.v}</dd>
                    </motion.div>
                ))}
            </motion.dl>

            <motion.div
                data-im="cue"
                className="hero-scroll"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: DUR.base, delay: 0.95, ease: EASE }}
                aria-hidden="true"
            >
                <span className="hero-scroll-label mono">Scroll</span>
                <span className="hero-scroll-track">
                    <span className="hero-scroll-beam" />
                </span>
            </motion.div>
        </section>
    );

    if (!story && !phone) return hero;

    return (
        <SceneTrack pin={200} className={story ? "scene-index" : "scene-index scene-index--phone"}>
            {(p) => (
                <>
                    <IndexMorph p={p} heroRef={heroRef} mode={heroTextMode} photo={CARD_PHOTO} phone={!story} />
                    {hero}
                </>
            )}
        </SceneTrack>
    );
}
